import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaGithub, FaExternalLinkAlt, FaTimes } from "react-icons/fa";
import { projects } from "../../data/portfolioData";
import { useLanguage } from "../../context/LanguageContext";
import "./Projects.css";

const Projects = () => {
  const [filter, setFilter] = useState("featured");
  const [selectedProject, setSelectedProject] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);
  const modalRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const { t } = useLanguage();

  const openModal = (project) => {
    lastFocusedRef.current = document.activeElement;
    setSelectedProject(project);
  };

  const closeModal = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedProject(null);
      setIsClosing(false);
    }, 280);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedProject) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const modal = modalRef.current;
    const focusableSelector =
      'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = modal?.querySelectorAll(focusableSelector);
    focusableElements?.[0]?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
        return;
      }

      if (event.key !== "Tab" || !modal) return;
      const focusable = Array.from(modal.querySelectorAll(focusableSelector));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      lastFocusedRef.current?.focus();
    };
  }, [selectedProject, closeModal]);

  const filtered =
    filter === "all"
      ? projects
      : filter === "featured"
      ? projects.filter((p) => p.featured)
      : projects;

  return (
    <section className="projects" id="projects" ref={sectionRef}>
      <div className="projects__container">
        <h2 className="section-title">{t.projects.title}</h2>
        <p className="section-subtitle">{t.projects.subtitle}</p>

        {/* Filters */}
        <div className="projects__filters">
          {["featured", "all"].map((f) => (
            <button
              key={f}
              className={`projects__filter ${filter === f ? "projects__filter--active" : ""}`}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
            >
              {f === "all" ? t.projects.all : t.projects.featured}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="projects__grid">
          {filtered.map((project, idx) => (
            <div
              key={project.id}
              className={`project-card ${visible ? "animate-fade-up" : ""}`}
              style={{ animationDelay: `${idx * 0.12}s` }}
            >
              <div className="project-card__image-wrapper">
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="project-card__image"
                    loading="lazy"
                  />
                ) : (
                  <div className="project-card__placeholder">
                    <span>{project.title}</span>
                  </div>
                )}
                <button
                  type="button"
                  className="project-card__overlay"
                  onClick={() => openModal(project)}
                  aria-label={`${t.projects.viewLabel} ${project.title}`}
                >
                  <span>{t.projects.view}</span>
                </button>
              </div>
              <div className="project-card__body">
                <h3 className="project-card__title">{project.title}</h3>
                <p className="project-card__desc">
                  {(t.projectDescriptions[project.id] || project.description).substring(0, 100)}...
                </p>
                <div className="project-card__tech">
                  {project.techStack.map((tech) => (
                    <span key={tech} className="tech-tag">
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="project-card__links">
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="GitHub"
                  >
                    <FaGithub />
                  </a>
                  {project.liveDemo && (
                    <a
                      href={project.liveDemo}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Live Demo"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modal ── */}
      {selectedProject && (
        <div
          className={`modal-backdrop${isClosing ? ' modal-backdrop--closing' : ''}`}
          onClick={closeModal}
        >
          <div
            ref={modalRef}
            className={`modal${isClosing ? ' modal--closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-modal-title"
          >
            <button
              type="button"
              className="modal__close"
              onClick={closeModal}
              aria-label={t.projects.close}
            >
              <FaTimes />
            </button>
            {selectedProject.image ? (
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="modal__image"
              />
            ) : (
              <div className="modal__placeholder">
                <span>{selectedProject.title}</span>
              </div>
            )}
            <div className="modal__body">
              <h3 id="project-modal-title">{selectedProject.title}</h3>
              <p>{t.projectDescriptions[selectedProject.id] || selectedProject.description}</p>
              <div className="project-card__tech">
                {selectedProject.techStack.map((tech) => (
                  <span key={tech} className="tech-tag">
                    {tech}
                  </span>
                ))}
              </div>
              <div className="modal__links">
                <a
                  href={selectedProject.github}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn--primary"
                >
                  <FaGithub /> {t.projects.github}
                </a>
                {selectedProject.liveDemo && (
                  <a
                    href={selectedProject.liveDemo}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn--outline"
                  >
                    <FaExternalLinkAlt /> {t.projects.live}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
